import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Loader2, Pause, Play, RotateCcw } from "lucide-react";

// Minimal asciinema v2 (.cast) player: JSONL header + [time, "o", data] events.
// Supports the SGR subset our recordings use (16/256 fg colors, bold, dim,
// reset) plus \r line-overwrite and ESC[K erase — enough for real CLI output
// without pulling in xterm.js.

type CastEvent = [number, string];

interface Cast {
  cols: number;
  rows: number;
  events: CastEvent[];
  duration: number;
}

const PALETTE: Record<number, string> = {
  30: "#3f4451", 31: "#f87171", 32: "#4ade80", 33: "#fbbf24",
  34: "#60a5fa", 35: "#e879f9", 36: "#22d3ee", 37: "#d4d4d8",
  90: "#6b7280", 91: "#fca5a5", 92: "#86efac", 93: "#fde68a",
  94: "#93c5fd", 95: "#f0abfc", 96: "#67e8f9", 97: "#ffffff",
};

const COLOR_256: Record<number, string> = {
  8: "#6b7280", 10: "#4ade80", 12: "#60a5fa", 13: "#e879f9", 14: "#22d3ee",
  39: "#38bdf8", 45: "#22d3ee", 48: "#34d399", 141: "#a78bfa", 156: "#bef264",
  205: "#f472b6", 208: "#fb923c", 213: "#f0abfc", 220: "#facc15", 244: "#9ca3af",
};

interface Style { color?: string; bold?: boolean; dim?: boolean }
interface Span { text: string; style: Style }

function parseCast(src: string): Cast {
  const lines = src.split("\n").filter(Boolean);
  let cols = 80;
  let rows = 24;
  const events: CastEvent[] = [];
  for (const line of lines) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(line);
    } catch {
      continue;
    }
    if (Array.isArray(parsed)) {
      const [t, kind, data] = parsed as [number, string, string];
      if (kind === "o" && typeof data === "string") events.push([t, data]);
    } else if (parsed && typeof parsed === "object") {
      const h = parsed as { width?: number; height?: number };
      if (h.width) cols = h.width;
      if (h.height) rows = h.height;
    }
  }
  const duration = events.length ? events[events.length - 1][0] : 0;
  return { cols, rows, events, duration };
}

// Feed raw output into a line buffer, applying \r, \n, ESC[K and SGR styling.
class Screen {
  lines: Span[][] = [[]];
  private col = 0;
  private style: Style = {};

  private row(): Span[] {
    return this.lines[this.lines.length - 1];
  }

  private applySgr(params: string) {
    const codes = params.length ? params.split(";").map(Number) : [0];
    for (let i = 0; i < codes.length; i++) {
      const c = codes[i];
      if (c === 0) this.style = {};
      else if (c === 1) this.style = { ...this.style, bold: true };
      else if (c === 2) this.style = { ...this.style, dim: true };
      else if (c === 22) this.style = { ...this.style, bold: false, dim: false };
      else if (c === 39) this.style = { ...this.style, color: undefined };
      else if (PALETTE[c]) this.style = { ...this.style, color: PALETTE[c] };
      else if (c === 38 && codes[i + 1] === 5) {
        const n = codes[i + 2];
        this.style = { ...this.style, color: COLOR_256[n] || "#d4d4d8" };
        i += 2;
      }
    }
  }

  private overwrite(text: string) {
    const row = this.row();
    let remaining = this.col;
    let idx = 0;
    while (idx < row.length && remaining >= row[idx].text.length) {
      remaining -= row[idx].text.length;
      idx++;
    }
    if (idx < row.length) {
      const keep = row[idx].text.slice(0, remaining);
      row.splice(idx, row.length - idx, ...(keep ? [{ text: keep, style: row[idx].style }] : []));
    }
    if (text) row.push({ text, style: this.style });
    this.col += text.length;
  }

  write(data: string) {
    let i = 0;
    while (i < data.length) {
      const ch = data[i];
      if (ch === "\x1b") {
        const m = /^\x1b\[([0-9;?]*)([A-Za-z])/.exec(data.slice(i));
        if (m) {
          if (m[2] === "m") this.applySgr(m[1]);
          else if (m[2] === "K") {
            // erase to end of line: overwrite() already truncates on next write,
            // but handle standalone erase too
            this.overwrite("");
          }
          i += m[0].length;
          continue;
        }
        i++;
        continue;
      }
      if (ch === "\n") {
        this.lines.push([]);
        this.col = 0;
        i++;
        continue;
      }
      if (ch === "\r") {
        this.col = 0;
        i++;
        continue;
      }
      let j = i;
      while (j < data.length && !"\x1b\n\r".includes(data[j])) j++;
      this.overwrite(data.slice(i, j));
      i = j;
    }
  }
}

function renderLines(screen: Screen, maxRows: number): Span[][] {
  const lines = screen.lines;
  return lines.slice(Math.max(0, lines.length - maxRows));
}

export default function TerminalCast({
  src,
  title = "app.nz — terminal",
  speed = 1,
  rows = 22,
  className = "",
  poster = "$ press play to replay this workflow",
}: {
  src: string;
  title?: string;
  speed?: number;
  rows?: number;
  className?: string;
  poster?: string;
}) {
  const [cast, setCast] = useState<Cast | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reducedMotion, setReducedMotion] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lines, setLines] = useState<Span[][]>([[{ text: poster, style: {} }]]);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const loopTimerRef = useRef<number | null>(null);
  const stateRef = useRef({ idx: 0, elapsed: 0, screen: new Screen(), raf: 0, last: 0 });
  const playingRef = useRef(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    setCast(null);
    fetch(src)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((text) => {
        if (!alive) return;
        const next = parseCast(text);
        if (next.events.length === 0) throw new Error("empty recording");
        setCast(next);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setError("Recording unavailable");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [src]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);

  const restart = useCallback(() => {
    if (loopTimerRef.current) window.clearTimeout(loopTimerRef.current);
    loopTimerRef.current = null;
    const st = stateRef.current;
    st.idx = 0;
    st.elapsed = 0;
    st.screen = new Screen();
    setProgress(0);
    setLines([[{ text: poster, style: {} }]]);
  }, [poster]);

  const tick = useCallback(
    (now: number) => {
      const st = stateRef.current;
      if (!playingRef.current || !cast) return;
      const dt = st.last ? (now - st.last) / 1000 : 0;
      st.last = now;
      st.elapsed += dt * speed;
      let advanced = false;
      while (st.idx < cast.events.length && cast.events[st.idx][0] <= st.elapsed) {
        st.screen.write(cast.events[st.idx][1]);
        st.idx++;
        advanced = true;
      }
      if (advanced) {
        setLines(renderLines(st.screen, rows));
        setProgress(Math.min(1, st.elapsed / (cast.duration || 1)));
      }
      if (st.idx >= cast.events.length) {
        setProgress(1);
        playingRef.current = false;
        setPlaying(false);
        // loop after a beat so the finished state is readable
        loopTimerRef.current = window.setTimeout(() => {
          loopTimerRef.current = null;
          if (!stateRef.current.raf && containerRef.current) {
            restart();
            playingRef.current = true;
            setPlaying(true);
            stateRef.current.last = 0;
            stateRef.current.raf = requestAnimationFrame(tick);
          }
        }, 3500);
        st.raf = 0;
        return;
      }
      st.raf = requestAnimationFrame(tick);
    },
    [cast, speed, rows, restart],
  );

  const play = useCallback(() => {
    if (!cast || playingRef.current) return;
    playingRef.current = true;
    setPlaying(true);
    setStarted(true);
    stateRef.current.last = 0;
    stateRef.current.raf = requestAnimationFrame(tick);
  }, [cast, tick]);

  const pause = useCallback(() => {
    playingRef.current = false;
    setPlaying(false);
    if (stateRef.current.raf) cancelAnimationFrame(stateRef.current.raf);
    stateRef.current.raf = 0;
  }, []);

  // autoplay once when scrolled into view
  useEffect(() => {
    if (!cast || started || reducedMotion || typeof IntersectionObserver === "undefined") return;
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          play();
          obs.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [cast, started, reducedMotion, play]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines]);

  useEffect(
    () => () => {
      playingRef.current = false;
      if (stateRef.current.raf) cancelAnimationFrame(stateRef.current.raf);
      if (loopTimerRef.current) window.clearTimeout(loopTimerRef.current);
    },
    [],
  );

  return (
    <div
      ref={containerRef}
      aria-label={`${title} terminal recording`}
      className={`min-w-0 max-w-full overflow-hidden rounded-2xl border border-white/12 bg-[#070b14] shadow-[0_0_60px_rgba(8,145,178,0.12)] ${className}`}
    >
      <div className="flex items-center gap-2 border-b border-white/10 bg-white/[0.03] px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-[#f87171]/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#fbbf24]/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#4ade80]/80" />
        <span className="ml-3 min-w-0 truncate font-mono text-xs text-white/55">{title}</span>
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            aria-label={playing ? "Pause" : "Play"}
            disabled={!cast}
            onClick={playing ? pause : play}
            className="rounded-md p-1.5 text-white/60 transition hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
          >
            {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          </button>
          <button
            type="button"
            aria-label="Restart"
            disabled={!cast}
            onClick={() => {
              pause();
              restart();
              play();
            }}
            className="rounded-md p-1.5 text-white/60 transition hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        role="log"
        aria-live="polite"
        aria-label="Terminal output"
        className="max-h-[22rem] overflow-y-hidden px-4 py-3 font-mono text-[13px] leading-6 text-[#d4d4d8] sm:max-h-none"
        style={{ height: `${rows * 1.5 + 1.5}rem` }}
        onClick={playing ? pause : play}
      >
        {loading ? (
          <div className="flex items-center gap-2 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /> Loading recording…</div>
        ) : error ? (
          <div role="status" className="flex items-center gap-2 text-amber-200/75"><AlertCircle className="h-4 w-4" /> {error}</div>
        ) : lines.map((line, li) => (
          <div key={li} className="whitespace-pre-wrap break-words">
            {line.length === 0
              ? " "
              : line.map((span, si) => (
                  <span
                    key={si}
                    style={{
                      color: span.style.color,
                      fontWeight: span.style.bold ? 600 : undefined,
                      opacity: span.style.dim ? 0.6 : undefined,
                    }}
                  >
                    {span.text}
                  </span>
                ))}
            {li === lines.length - 1 && playing && (
              <span className="ml-0.5 inline-block h-4 w-2 translate-y-0.5 animate-pulse bg-[#22d3ee]" />
            )}
          </div>
        ))}
      </div>
      <div className="h-0.5 w-full bg-white/[0.06]">
        <div
          className="h-full bg-gradient-to-r from-cyan-400 to-fuchsia-400 transition-[width] duration-300"
          style={{ width: `${progress * 100}%` }}
        />
      </div>
    </div>
  );
}
